import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiDownload,
  FiShoppingCart,
  FiPlus,
  FiMinus,
  FiRotateCw,
  FiFileText,
  FiCheck,
  FiEdit,
  FiEye,
  FiAlertTriangle,
  FiPackage,
  FiTruck,
  FiShoppingBag,
} from 'react-icons/fi';

function InventoryProcurement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [items, setItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showBulkOrderModal, setShowBulkOrderModal] = useState(false);
  const [showQuickOrderModal, setShowQuickOrderModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Advanced dummy data for vegetables, food, and housekeeping products in Indian Rupees
  const initialItems = [
    // Vegetables
    {
      id: 1,
      sku: 'VEG-TOMATO-001',
      name: 'Fresh Tomatoes',
      description: 'Organic Roma Tomatoes',
      category: 'Vegetables',
      unitOfMeasure: 'Kg',
      cost: 80,
      currentStock: 8,
      minStock: 15,
      maxStock: 100,
      reorderPoint: 20,
      location: 'COLD-A01',
      supplier: 'Fresh Farms Co.',
      lastRestocked: '2024-01-20',
      shelfLife: 7,
    },
    {
      id: 2,
      sku: 'VEG-POTATO-002',
      name: 'Potatoes',
      description: 'Russet Potatoes',
      category: 'Vegetables',
      unitOfMeasure: 'Kg',
      cost: 40,
      currentStock: 45,
      minStock: 20,
      maxStock: 200,
      reorderPoint: 30,
      location: 'DRY-B02',
      supplier: 'Valley Growers',
      lastRestocked: '2024-01-25',
      shelfLife: 30,
    },
    {
      id: 3,
      sku: 'VEG-CARROT-003',
      name: 'Carrots',
      description: 'Organic Baby Carrots',
      category: 'Vegetables',
      unitOfMeasure: 'Kg',
      cost: 60,
      currentStock: 12,
      minStock: 10,
      maxStock: 80,
      reorderPoint: 15,
      location: 'COLD-A03',
      supplier: 'Fresh Farms Co.',
      lastRestocked: '2024-01-28',
      shelfLife: 14,
    },
    // Food Items
    {
      id: 4,
      sku: 'FD-RICE-001',
      name: 'Basmati Rice',
      description: 'Premium Basmati Rice 5kg',
      category: 'Food',
      unitOfMeasure: 'Bag',
      cost: 450,
      currentStock: 25,
      minStock: 10,
      maxStock: 100,
      reorderPoint: 15,
      location: 'DRY-C01',
      supplier: 'Global Foods Inc.',
      lastRestocked: '2024-01-22',
      shelfLife: 365,
    },
    {
      id: 5,
      sku: 'FD-PASTA-002',
      name: 'Spaghetti Pasta',
      description: 'Whole Wheat Spaghetti 500g',
      category: 'Food',
      unitOfMeasure: 'Packet',
      cost: 120,
      currentStock: 8,
      minStock: 15,
      maxStock: 150,
      reorderPoint: 20,
      location: 'DRY-C02',
      supplier: 'Italian Delights',
      lastRestocked: '2024-01-26',
      shelfLife: 180,
    },
    {
      id: 6,
      sku: 'FD-OIL-003',
      name: 'Olive Oil',
      description: 'Extra Virgin Olive Oil 1L',
      category: 'Food',
      unitOfMeasure: 'Bottle',
      cost: 650,
      currentStock: 32,
      minStock: 8,
      maxStock: 80,
      reorderPoint: 12,
      location: 'DRY-C03',
      supplier: 'Mediterranean Imports',
      lastRestocked: '2024-01-24',
      shelfLife: 540,
    },
    // Housekeeping Products
    {
      id: 7,
      sku: 'HK-SHAMPOO-001',
      name: 'Anti-Dandruff Shampoo',
      description: 'Clinical Strength Shampoo 400ml',
      category: 'Housekeeping',
      unitOfMeasure: 'Bottle',
      cost: 320,
      currentStock: 6,
      minStock: 12,
      maxStock: 60,
      reorderPoint: 15,
      location: 'CLEAN-D01',
      supplier: 'Personal Care Co.',
      lastRestocked: '2024-01-21',
      shelfLife: 730,
    },
    {
      id: 8,
      sku: 'HK-SHAVE-002',
      name: 'Shaving Kit',
      description: 'Premium Shaving Kit with Razor',
      category: 'Housekeeping',
      unitOfMeasure: 'Kit',
      cost: 890,
      currentStock: 4,
      minStock: 8,
      maxStock: 40,
      reorderPoint: 10,
      location: 'CLEAN-D02',
      supplier: 'Grooming Essentials',
      lastRestocked: '2024-01-19',
      shelfLife: 1095,
    },
    {
      id: 9,
      sku: 'HK-DETERGENT-003',
      name: 'Laundry Detergent',
      description: 'Liquid Detergent 2L',
      category: 'Housekeeping',
      unitOfMeasure: 'Bottle',
      cost: 520,
      currentStock: 18,
      minStock: 6,
      maxStock: 50,
      reorderPoint: 8,
      location: 'CLEAN-D03',
      supplier: 'Clean Home Products',
      lastRestocked: '2024-01-27',
      shelfLife: 730,
    },
    {
      id: 10,
      sku: 'HK-SOAP-004',
      name: 'Hand Soap',
      description: 'Antibacterial Hand Soap Refill',
      category: 'Housekeeping',
      unitOfMeasure: 'Refill',
      cost: 180,
      currentStock: 22,
      minStock: 10,
      maxStock: 100,
      reorderPoint: 15,
      location: 'CLEAN-D04',
      supplier: 'Personal Care Co.',
      lastRestocked: '2024-01-23',
      shelfLife: 730,
    },
  ];

  useEffect(() => {
    const itemsWithStatus = initialItems.map((item) => ({
      ...item,
      status: getStockStatus(item),
    }));
    setItems(itemsWithStatus);
    updateLowStockItems(itemsWithStatus);
  }, []);

  const updateLowStockItems = (itemsList) => {
    const lowStock = itemsList.filter(
      (item) => item.status === 'low-stock' || item.status === 'reorder'
    );
    setLowStockItems(lowStock);
  };

  const getStockStatus = (item) => {
    if (item.currentStock <= item.minStock) {
      return 'low-stock';
    } else if (item.currentStock <= item.reorderPoint) {
      return 'reorder';
    } else if (item.currentStock >= item.maxStock * 0.9) {
      return 'over-stock';
    }
    return 'normal';
  };

  const handleStockAdjustment = (itemId, adjustment) => {
    const updatedItems = items.map((item) => {
      if (item.id === itemId) {
        const newStock = Math.max(0, item.currentStock + adjustment);
        return {
          ...item,
          currentStock: newStock,
          status: getStockStatus({ ...item, currentStock: newStock }),
        };
      }
      return item;
    });
    setItems(updatedItems);
    updateLowStockItems(updatedItems);
  };

  const handleRestock = (itemId) => {
    const updatedItems = items.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          currentStock: item.maxStock,
          lastRestocked: new Date().toISOString().split('T')[0],
          status: 'normal',
        };
      }
      return item;
    });
    setItems(updatedItems);
    updateLowStockItems(updatedItems);
  };

  const handleOrderMore = (itemId) => {
    const item = items.find((item) => item.id === itemId);
    const orderQuantity = item.maxStock - item.currentStock;
    alert(`Ordering ${orderQuantity} ${item.unitOfMeasure} of ${item.name} from ${item.supplier}`);
  };

  const handleBulkOrder = () => {
    const lowStockItemsToOrder = lowStockItems.map((item) => ({
      name: item.name,
      quantity: item.maxStock - item.currentStock,
      unit: item.unitOfMeasure,
      supplier: item.supplier,
    }));
    setSelectedItems(lowStockItemsToOrder);
    setShowBulkOrderModal(true);
  };

  const handleCreateBulkOrder = () => {
    alert(`Creating bulk order for ${selectedItems.length} items`);
    setShowBulkOrderModal(false);
    setSelectedItems([]);
  };

  const handleExportReport = () => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      'SKU,Name,Category,Current Stock,Min Stock,Max Stock,Unit Cost,Status\n' +
      items
        .map(
          (item) =>
            `"${item.sku}","${item.name}","${item.category}",${item.currentStock},${item.minStock},${item.maxStock},${item.cost},"${item.status}"`
        )
        .join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'inventory_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleQuickOrder = () => {
    setShowQuickOrderModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'low-stock':
        return 'bg-gray-100 text-black border border-gray-300';
      case 'reorder':
        return 'bg-gray-200 text-black border border-gray-400';
      case 'over-stock':
        return 'bg-gray-300 text-black border border-gray-500';
      default:
        return 'bg-white text-black border border-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'low-stock':
        return 'Critical Low';
      case 'reorder':
        return 'Need Reorder';
      case 'over-stock':
        return 'Overstocked';
      default:
        return 'In Stock';
    }
  };

  const getStockPercentage = (item) => {
    return (item.currentStock / item.maxStock) * 100;
  };

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['All', 'Vegetables', 'Food', 'Housekeeping'];

  // Procurement Data
  const purchaseOrders = [
    {
      id: 1,
      poNumber: 'PO-2024-001',
      supplier: 'Fresh Farms Co.',
      items: ['Tomatoes', 'Carrots'],
      totalValue: 11200,
      status: 'Approved',
      date: '2024-01-15',
      deliveryDate: '2024-01-25',
    },
    {
      id: 2,
      poNumber: 'PO-2024-002',
      supplier: 'Personal Care Co.',
      items: ['Shampoo', 'Hand Soap'],
      totalValue: 15000,
      status: 'Pending',
      date: '2024-01-28',
      deliveryDate: '2024-02-05',
    },
  ];

  const requisitions = [
    {
      id: 1,
      reqNumber: 'REQ-2024-001',
      requestedBy: 'Kitchen Manager',
      items: ['Potatoes', 'Rice'],
      totalValue: 8500,
      status: 'Approved',
      date: '2024-01-20',
    },
  ];

  return (
    <div>
      <div className="p-6 bg-gray-100 min-h-screen">
        <header className="flex justify-between items-center mb-3 bg-white p-3">
          <h1 className="text-black text-xl font-bold ml-2">Supply Chain and Procurement</h1>
          <div className="flex space-x-6">
            <button
              onClick={() => navigate('/dashboard/core-modules/supply-chain/vendor-management')}
              className="btn"
            >
              Vendor Management
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/supply-chain/inventory-procurement')}
              className="btn text-black font-bold"
            >
              Inventory and Procurement
            </button>
            <button
              onClick={() =>
                navigate('/dashboard/core-modules/supply-chain/cost-control-budgeting')
              }
              className="btn"
            >
              Cost Control and Budgeting
            </button>
          </div>
        </header>

        {/* Tabs Navigation */}
        <div className="bg-white mb-6">
          <div className="flex border-b">
            <button
              className={`px-6 py-3 font-medium flex items-center ${activeTab === 'inventory' ? 'border-b-2 border-black text-black' : 'text-gray-600'}`}
              onClick={() => setActiveTab('inventory')}
            >
              <FiPackage className="mr-2" />
              Inventory Management
            </button>
            <button
              className={`px-6 py-3 font-medium flex items-center ${activeTab === 'procurement' ? 'border-b-2 border-black text-black' : 'text-gray-600'}`}
              onClick={() => setActiveTab('procurement')}
            >
              <FiShoppingCart className="mr-2" />
              Procurement
            </button>
            <button
              className={`px-6 py-3 font-medium flex items-center ${activeTab === 'requisitions' ? 'border-b-2 border-black text-black' : 'text-gray-600'}`}
              onClick={() => setActiveTab('requisitions')}
            >
              <FiFileText className="mr-2" />
              Requisitions
            </button>
          </div>
        </div>

        {/* Inventory Management Tab */}
        {activeTab === 'inventory' && (
          <div className="bg-white p-6">
            {/* Header with Search and Filters */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search items or SKU..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded w-80 text-black bg-white"
                  />
                  <FiSearch className="absolute left-3 top-2.5 text-gray-500" />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border border-gray-300 rounded px-4 py-2 text-black bg-white"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleExportReport}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors flex items-center"
                >
                  <FiDownload className="mr-2" />
                  Export Report
                </button>
                <button
                  onClick={handleQuickOrder}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors flex items-center"
                >
                  <FiShoppingBag className="mr-2" />
                  Quick Order
                </button>
              </div>
            </div>

            {/* Inventory Overview Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded border border-gray-300">
                <div className="text-sm text-gray-600 flex items-center">
                  <FiPackage className="mr-2" />
                  Total Items
                </div>
                <div className="text-2xl font-bold text-black">{items.length}</div>
                <div className="text-xs text-gray-500 mt-1">Across all categories</div>
              </div>
              <div className="bg-white p-4 rounded border border-gray-300">
                <div className="text-sm text-gray-600 flex items-center">
                  <FiAlertTriangle className="mr-2" />
                  Low Stock Alert
                </div>
                <div className="text-2xl font-bold text-black">{lowStockItems.length}</div>
                <div className="text-xs text-gray-500 mt-1">Need immediate attention</div>
              </div>
              <div className="bg-white p-4 rounded border border-gray-300">
                <div className="text-sm text-gray-600 flex items-center">
                  <FiShoppingCart className="mr-2" />
                  Total Inventory Value
                </div>
                <div className="text-2xl font-bold text-black">
                  ₹
                  {items
                    .reduce((sum, item) => sum + item.cost * item.currentStock, 0)
                    .toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">Current stock value</div>
              </div>
              <div className="bg-white p-4 rounded border border-gray-300">
                <div className="text-sm text-gray-600 flex items-center">
                  <FiFileText className="mr-2" />
                  Categories
                </div>
                <div className="text-2xl font-bold text-black">3</div>
                <div className="text-xs text-gray-500 mt-1">Active product categories</div>
              </div>
            </div>

            {/* Low Stock Alert Banner */}
            {lowStockItems.length > 0 && (
              <div className="mb-6 p-4 bg-gray-100 border border-gray-400 rounded">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FiAlertTriangle className="text-black text-lg mr-3" />
                    <div>
                      <h3 className="font-semibold text-black">Low Stock Alert</h3>
                      <p className="text-black text-sm">
                        {lowStockItems.length} items are below minimum stock levels and need
                        reordering.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleBulkOrder}
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm flex items-center"
                  >
                    <FiShoppingCart className="mr-2" />
                    Create Bulk Order
                  </button>
                </div>
              </div>
            )}

            {/* Inventory Table */}
            <div className="overflow-x-auto bg-white rounded border border-gray-300">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                  <tr>
                    <th className="px-6 py-4">Product Info</th>
                    <th className="px-6 py-4">Stock Level</th>
                    <th className="px-6 py-4">Inventory Metrics</th>
                    <th className="px-6 py-4">Supplier & Location</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr
                      key={item.id}
                      className="bg-white border-b border-gray-300 hover:bg-gray-50"
                    >
                      {/* Product Info */}
                      <td className="px-6 py-4">
                        <div className="font-semibold text-black">{item.name}</div>
                        <div className="text-gray-600 text-xs font-mono">{item.sku}</div>
                        <div className="text-gray-500 text-xs mt-1">{item.description}</div>
                        <div className="text-xs text-gray-400 mt-1">{item.category}</div>
                      </td>

                      {/* Stock Level */}
                      <td className="px-6 py-4">
                        <div className="font-bold text-lg text-black">
                          {item.currentStock} {item.unitOfMeasure}
                        </div>
                        <div className="text-gray-600 text-xs">
                          Min: {item.minStock} | Max: {item.maxStock}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div
                            className={`h-2 rounded-full ${
                              getStockPercentage(item) < 20
                                ? 'bg-gray-800'
                                : getStockPercentage(item) < 50
                                  ? 'bg-gray-600'
                                  : getStockPercentage(item) > 90
                                    ? 'bg-gray-400'
                                    : 'bg-gray-700'
                            }`}
                            style={{ width: `${Math.min(getStockPercentage(item), 100)}%` }}
                          ></div>
                        </div>
                      </td>

                      {/* Inventory Metrics */}
                      <td className="px-6 py-4">
                        <div className="text-black font-medium">₹{item.cost}</div>
                        <div className="text-gray-600 text-xs">Unit Cost</div>
                        <div className="text-black font-medium mt-2">
                          ₹{(item.cost * item.currentStock).toLocaleString()}
                        </div>
                        <div className="text-gray-600 text-xs">Stock Value</div>
                        <div className="text-gray-500 text-xs mt-2">
                          Reorder: {item.reorderPoint}
                        </div>
                      </td>

                      {/* Supplier & Location */}
                      <td className="px-6 py-4">
                        <div className="text-black text-sm">{item.supplier}</div>
                        <div className="text-gray-600 text-xs font-mono">{item.location}</div>
                        <div className="text-gray-500 text-xs mt-2">Last: {item.lastRestocked}</div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}
                        >
                          {getStatusText(item.status)}
                        </span>
                        {item.shelfLife && (
                          <div className="text-gray-500 text-xs mt-2">
                            Shelf: {item.shelfLife} days
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-2">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleStockAdjustment(item.id, 1)}
                              className="px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-800 flex items-center"
                            >
                              <FiPlus size={12} />
                            </button>
                            <button
                              onClick={() => handleStockAdjustment(item.id, -1)}
                              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 flex items-center"
                            >
                              <FiMinus size={12} />
                            </button>
                          </div>
                          <button
                            onClick={() => handleRestock(item.id)}
                            className="px-3 py-1 bg-gray-800 text-white rounded text-xs hover:bg-gray-900 flex items-center"
                          >
                            <FiRotateCw size={12} className="mr-1" />
                            Restock
                          </button>
                          <button
                            onClick={() => handleOrderMore(item.id)}
                            className="px-3 py-1 bg-black text-white rounded text-xs hover:bg-gray-800 flex items-center"
                          >
                            <FiShoppingCart size={12} className="mr-1" />
                            Order
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Procurement Tab */}
        {activeTab === 'procurement' && (
          <div className="bg-white p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-black">Purchase Orders</h2>
              <button className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors flex items-center">
                <FiPlus className="mr-2" />
                Create New PO
              </button>
            </div>

            {/* Purchase Orders Table */}
            <div className="overflow-x-auto bg-white rounded border border-gray-300">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                  <tr>
                    <th className="px-6 py-4">PO Number</th>
                    <th className="px-6 py-4">Supplier</th>
                    <th className="px-6 py-4">Items</th>
                    <th className="px-6 py-4">Total Value</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Delivery Date</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="bg-white border-b border-gray-300 hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-black">{po.poNumber}</td>
                      <td className="px-6 py-4 text-black">{po.supplier}</td>
                      <td className="px-6 py-4">
                        {po.items.map((item, index) => (
                          <div key={index} className="text-sm text-black">
                            • {item}
                          </div>
                        ))}
                      </td>
                      <td className="px-6 py-4 text-black font-medium">
                        ₹{po.totalValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{po.date}</td>
                      <td className="px-6 py-4 text-gray-600">{po.deliveryDate}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            po.status === 'Approved'
                              ? 'bg-gray-200 text-black border border-gray-400'
                              : po.status === 'Pending'
                                ? 'bg-gray-100 text-black border border-gray-300'
                                : 'bg-white text-black border border-gray-300'
                          }`}
                        >
                          {po.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button className="text-black hover:text-gray-700 mr-3 text-sm flex items-center">
                          <FiEye className="mr-1" />
                          View
                        </button>
                        <button className="text-black hover:text-gray-700 text-sm flex items-center">
                          <FiEdit className="mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Requisitions Tab */}
        {activeTab === 'requisitions' && (
          <div className="bg-white p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-black">Purchase Requisitions</h2>
              <button className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors flex items-center">
                <FiPlus className="mr-2" />
                Create Requisition
              </button>
            </div>

            {/* Requisitions Table */}
            <div className="overflow-x-auto bg-white rounded border border-gray-300">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                  <tr>
                    <th className="px-6 py-4">Req Number</th>
                    <th className="px-6 py-4">Requested By</th>
                    <th className="px-6 py-4">Items</th>
                    <th className="px-6 py-4">Total Value</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requisitions.map((req) => (
                    <tr key={req.id} className="bg-white border-b border-gray-300 hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-black">{req.reqNumber}</td>
                      <td className="px-6 py-4 text-black">{req.requestedBy}</td>
                      <td className="px-6 py-4">
                        {req.items.map((item, index) => (
                          <div key={index} className="text-sm text-black">
                            • {item}
                          </div>
                        ))}
                      </td>
                      <td className="px-6 py-4 text-black font-medium">
                        ₹{req.totalValue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{req.date}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            req.status === 'Approved'
                              ? 'bg-gray-200 text-black border border-gray-400'
                              : 'bg-gray-100 text-black border border-gray-300'
                          }`}
                        >
                          {req.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button className="text-black hover:text-gray-700 mr-3 text-sm flex items-center">
                          <FiEye className="mr-1" />
                          View
                        </button>
                        <button className="text-black hover:text-gray-700 text-sm flex items-center">
                          <FiCheck className="mr-1" />
                          Approve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bulk Order Modal */}
        {showBulkOrderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-4xl">
              <h3 className="text-lg font-semibold mb-4 text-black flex items-center">
                <FiShoppingCart className="mr-2" />
                Create Bulk Order
              </h3>
              <div className="max-h-96 overflow-y-auto mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Item</th>
                      <th className="px-4 py-2 text-left">Supplier</th>
                      <th className="px-4 py-2 text-left">Order Quantity</th>
                      <th className="px-4 py-2 text-left">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-4 py-2">{item.name}</td>
                        <td className="px-4 py-2">{item.supplier}</td>
                        <td className="px-4 py-2">{item.quantity}</td>
                        <td className="px-4 py-2">{item.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkOrderModal(false)}
                  className="px-4 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBulkOrder}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors flex items-center"
                >
                  <FiCheck className="mr-2" />
                  Create Order
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Order Modal */}
        {showQuickOrderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-2xl">
              <h3 className="text-lg font-semibold mb-4 text-black flex items-center">
                <FiShoppingBag className="mr-2" />
                Quick Order
              </h3>
              <p className="text-gray-600 mb-4">Select items to order quickly:</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowQuickOrderModal(false)}
                  className="px-4 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowQuickOrderModal(false)}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InventoryProcurement;
